import { useTranslation } from 'react-i18next';
import type {
  WebsiteAnalyticsDeviceItem,
  WebsiteAnalyticsSourceItem,
  WebsiteAnalyticsTopPageItem,
  WebsiteAnalyticsTrendItem,
} from '../types';
import { formatTrafficSource } from '../utils/format';
import { AnalyticsPanel, AnalyticsTable } from './AnalyticsPanel';

interface ProductAnalyticsTrendsPanelProps {
  items: WebsiteAnalyticsTrendItem[];
  locale: string;
  isLoading: boolean;
  isError: boolean;
}

export function ProductAnalyticsTrendsPanel({
  items,
  locale,
  isLoading,
  isError,
}: ProductAnalyticsTrendsPanelProps) {
  const { t } = useTranslation();

  const sortedItems = [...items].sort((a, b) => b.date.localeCompare(a.date));

  return (
    <AnalyticsPanel
      title={t('websiteActivity.products.trendsTitle')}
      isLoading={isLoading}
      isError={isError}
      isEmpty={sortedItems.length === 0}
      errorMessage={t('websiteActivity.products.trendsLoadError')}
      emptyTitle={t('websiteActivity.products.trendsEmptyTitle')}
      emptyMessage={t('websiteActivity.products.trendsEmptyMessage')}
    >
      <AnalyticsTable
        columns={[
          { key: 'date', label: t('websiteActivity.products.columns.date') },
          {
            key: 'pageViews',
            label: t('websiteActivity.products.metrics.pageViews'),
            align: 'end',
          },
          {
            key: 'registrations',
            label: t('websiteActivity.products.metrics.registrations'),
            align: 'end',
          },
          {
            key: 'freeTrials',
            label: t('websiteActivity.products.metrics.freeTrials'),
            align: 'end',
          },
          {
            key: 'failedTrials',
            label: t('websiteActivity.products.metrics.failedTrials'),
            align: 'end',
          },
          { key: 'payments', label: t('websiteActivity.products.metrics.payments'), align: 'end' },
        ]}
        rows={sortedItems.map((item) => ({
          key: item.date,
          cells: [
            new Date(`${item.date}T00:00:00Z`).toLocaleDateString(locale, {
              timeZone: 'UTC',
              year: 'numeric',
              month: 'short',
              day: 'numeric',
            }),
            item.pageViews,
            item.registrations,
            item.freeTrials,
            item.failedTrials,
            item.payments,
          ],
        }))}
      />
    </AnalyticsPanel>
  );
}

interface ProductAnalyticsTopPagesPanelProps {
  items: WebsiteAnalyticsTopPageItem[];
  isLoading: boolean;
  isError: boolean;
}

export function ProductAnalyticsTopPagesPanel({
  items,
  isLoading,
  isError,
}: ProductAnalyticsTopPagesPanelProps) {
  const { t } = useTranslation();

  return (
    <AnalyticsPanel
      title={t('websiteActivity.products.topPagesTitle')}
      isLoading={isLoading}
      isError={isError}
      isEmpty={items.length === 0}
      errorMessage={t('websiteActivity.products.topPagesLoadError')}
      emptyTitle={t('websiteActivity.products.topPagesEmptyTitle')}
      emptyMessage={t('websiteActivity.products.topPagesEmptyMessage')}
    >
      <AnalyticsTable
        columns={[
          { key: 'pagePath', label: t('websiteActivity.products.columns.pagePath') },
          { key: 'count', label: t('websiteActivity.products.columns.count'), align: 'end' },
        ]}
        rows={items.map((item, index) => ({
          key: `${item.productSlug}-${item.pagePath}-${index}`,
          cells: [item.pagePath, item.count],
        }))}
      />
    </AnalyticsPanel>
  );
}

interface ProductAnalyticsSourcesPanelProps {
  items: WebsiteAnalyticsSourceItem[];
  isLoading: boolean;
  isError: boolean;
}

export function ProductAnalyticsSourcesPanel({
  items,
  isLoading,
  isError,
}: ProductAnalyticsSourcesPanelProps) {
  const { t } = useTranslation();

  return (
    <AnalyticsPanel
      title={t('websiteActivity.products.sourcesTitle')}
      isLoading={isLoading}
      isError={isError}
      isEmpty={items.length === 0}
      errorMessage={t('websiteActivity.products.sourcesLoadError')}
      emptyTitle={t('websiteActivity.products.sourcesEmptyTitle')}
      emptyMessage={t('websiteActivity.products.sourcesEmptyMessage')}
    >
      <AnalyticsTable
        columns={[
          { key: 'source', label: t('websiteActivity.products.columns.source') },
          { key: 'count', label: t('websiteActivity.products.columns.count'), align: 'end' },
        ]}
        rows={items.map((item, index) => ({
          key: `${item.productSlug}-${index}`,
          cells: [formatTrafficSource(item.utmSource, item.referrer), item.count],
        }))}
      />
    </AnalyticsPanel>
  );
}

interface ProductAnalyticsDevicesPanelProps {
  items: WebsiteAnalyticsDeviceItem[];
  isLoading: boolean;
  isError: boolean;
}

export function ProductAnalyticsDevicesPanel({
  items,
  isLoading,
  isError,
}: ProductAnalyticsDevicesPanelProps) {
  const { t } = useTranslation();

  return (
    <AnalyticsPanel
      title={t('websiteActivity.products.devicesTitle')}
      isLoading={isLoading}
      isError={isError}
      isEmpty={items.length === 0}
      errorMessage={t('websiteActivity.products.devicesLoadError')}
      emptyTitle={t('websiteActivity.products.devicesEmptyTitle')}
      emptyMessage={t('websiteActivity.products.devicesEmptyMessage')}
    >
      <AnalyticsTable
        columns={[
          { key: 'deviceType', label: t('websiteActivity.products.columns.deviceType') },
          { key: 'count', label: t('websiteActivity.products.columns.count'), align: 'end' },
        ]}
        rows={items.map((item, index) => ({
          key: `${item.productSlug}-${item.deviceType ?? 'unknown'}-${index}`,
          cells: [item.deviceType ?? t('websiteActivity.products.unknownDevice'), item.count],
        }))}
      />
    </AnalyticsPanel>
  );
}
